import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Select } from "../ui";
import {
  clearSiteValue,
  putSiteValue,
  setSiteMode,
  type Mode,
  type SiteEntry,
  type SiteField,
} from "./siteconf";

/** `DRANG_LLM_MODEL_2` and `DRANG_LLM_MODEL` share one label. */
const labelKey = (name: string) => `siteconf.field.${name.replace(/_2$/, "")}`;

/**
 * Which value counts, as one choice beside the field: off (or the built-in default), the `.env` value, or the
 * value entered here. A choice without a value is greyed out; typing into the field chooses "entered here".
 */
export function ModeSelect({
  entry,
  label,
  offLabel,
  onChanged,
}: {
  entry: SiteEntry;
  label: string;
  /** "Off", or "Default (8192)" for fields with a built-in value */
  offLabel?: string;
  onChanged(): void;
}) {
  const { t } = useTranslation();
  const mode: Mode =
    entry.mode ?? (entry.source === "settings" ? "value" : entry.source === "env" ? "env" : "off");
  return (
    <Select
      compact
      className="w-auto shrink-0"
      aria-label={t("siteconf.modeLabel", { name: label })}
      value={mode}
      onChange={(e) => void setSiteMode(entry.name, e.target.value as Mode).then(onChanged)}
      data-testid="siteconf-mode"
      data-mode={mode}
    >
      <option value="off">{offLabel ?? t("siteconf.mode.off")}</option>
      <option value="env" disabled={!entry.envSet}>
        {t("siteconf.mode.env")}
      </option>
      <option value="value" disabled={!entry.hasValue}>
        {t("siteconf.mode.value")}
      </option>
    </Select>
  );
}

/** A write-only input: the value is never shown again once saved. */
export function SecretInput({
  entry,
  label,
  onChanged,
}: {
  entry: SiteEntry;
  label: string;
  onChanged(): void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (!value) return;
    try {
      await putSiteValue(entry.name, value);
      setValue("");
      setError(null);
      onChanged();
    } catch (e) {
      setError(t(`errors.${(e as Error).message}`, { defaultValue: t("error.generic") }));
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <Input
          compact
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          aria-label={label}
          placeholder={entry.source === "unset" ? t("siteconf.enterSecret") : t("siteconf.replaceSecret")}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void save();
            }
          }}
          data-testid="secret-input"
          data-name={entry.name}
        />
        <Button size="sm" variant="secondary" disabled={!value} onClick={() => void save()}>
          {t("siteconf.save")}
        </Button>
        <ModeSelect entry={entry} label={label} onChanged={onChanged} />
      </div>
      {error && (
        <p role="alert" className="text-[12.5px] text-err">
          {error}
        </p>
      )}
    </div>
  );
}

/** "Off" — or, for a field with a built-in value, "Default (8192)": what "off" really gives. */
function offLabel(field: SiteField, t: (k: string, o?: Record<string, unknown>) => string): string {
  const d = field.default;
  if (!d || d === "false" || d === "none") return t("siteconf.mode.off");
  const shown = field.kind === "enum" ? t(`siteconf.option.${d}`, { defaultValue: d }) : d;
  return t("siteconf.mode.default", { value: shown });
}

/** One setting from the registry, saved as soon as it is changed (text: on Enter or leaving the field). */
export function SiteFieldRow({ field, onChanged }: { field: SiteField; onChanged(): void }) {
  const { t } = useTranslation();
  const label = t(labelKey(field.name));
  const id = `site-${field.name}`;
  const current = field.value ?? "";
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = async (v: string) => {
    if (v === current && field.source === "settings" && field.mode === "value") return;
    try {
      if (v === "") await clearSiteValue(field.name);
      else await putSiteValue(field.name, v);
      setDraft(null);
      setError(null);
      onChanged();
    } catch (e) {
      setError(t(`errors.${(e as Error).message}`, { defaultValue: t("error.generic") }));
    }
  };

  let control;
  if (field.kind === "secret")
    return (
      <div className="flex flex-col gap-0.5 sm:col-span-2" data-testid="site-field" data-name={field.name}>
        <span className="text-[12px] text-neutral-500">{label}</span>
        <SecretInput entry={field} label={label} onChanged={onChanged} />
      </div>
    );
  if (field.kind === "bool") {
    // yes/no settings: one choice, no checkbox — Off, the .env value, or On (both stored here)
    const on = ["1", "true", "yes"].includes(current.toLowerCase());
    const choice = field.mode === "env" ? "env" : on ? "on" : "off";
    control = (
      <Select
        compact
        id={id}
        className="w-auto"
        value={choice}
        data-testid="siteconf-bool"
        data-mode={choice}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "env") void setSiteMode(field.name, "env").then(onChanged);
          else void save(v === "on" ? "true" : "false");
        }}
      >
        <option value="off">{t("siteconf.mode.off")}</option>
        <option value="env" disabled={!field.envSet}>
          {t("siteconf.mode.env")}
        </option>
        <option value="on">{t("siteconf.mode.on")}</option>
      </Select>
    );
  } else if (field.kind === "enum")
    control = (
      <Select compact id={id} className="w-full" value={current} onChange={(e) => void save(e.target.value)}>
        {!field.options.includes(current) && <option value={current}>{current || "—"}</option>}
        {field.options.map((o) => (
          <option key={o} value={o}>
            {t(`siteconf.option.${o}`, { defaultValue: o })}
          </option>
        ))}
      </Select>
    );
  else
    control = (
      <Input
        compact
        id={id}
        inputMode={field.kind === "int" ? "numeric" : undefined}
        spellCheck={false}
        value={draft ?? current}
        placeholder={field.default}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => draft !== null && void save(draft.trim())}
        onKeyDown={(e) => {
          if (e.key === "Enter" && draft !== null) {
            e.preventDefault();
            void save(draft.trim());
          }
        }}
      />
    );
  return (
    <div className="flex min-w-0 flex-col gap-0.5" data-testid="site-field" data-name={field.name}>
      <label htmlFor={id} className="truncate text-[12px] text-neutral-500">
        {label}
      </label>
      <div className="flex items-center gap-1.5">
        {control}
        {field.kind !== "bool" && (
          <ModeSelect entry={field} label={label} offLabel={offLabel(field, t)} onChanged={onChanged} />
        )}
      </div>
      {error && (
        <p role="alert" className="text-[12.5px] text-err">
          {error}
        </p>
      )}
    </div>
  );
}

/** A registry group, hiding fields whose `when` condition does not hold. */
export function SiteGroup({
  fields,
  onChanged,
  testId,
}: {
  fields: SiteField[];
  onChanged(): void;
  testId?: string;
}) {
  const valueOf = (name: string) => fields.find((f) => f.name === name)?.value ?? "";
  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2" data-testid={testId}>
      {fields
        .filter((f) => !f.when || f.when.values.includes(valueOf(f.when.name)))
        .map((f) => (
          <SiteFieldRow key={`${f.name}|${f.source}|${f.value ?? ""}`} field={f} onChanged={onChanged} />
        ))}
    </div>
  );
}
